import { forwardRef, lazy, Suspense, useEffect, useState } from 'react'
import { ContentState, convertFromHTML, EditorState } from 'draft-js'
import type {
  Editor as WYSIWYGEditor,
  EditorProps,
  RawDraftContentState,
} from 'react-draft-wysiwyg'
import draftToHtml from 'draftjs-to-html'

export interface RichTextEditorProps
  extends Pick<
    EditorProps,
    | 'toolbarStyle'
    | 'toolbarClassName'
    | 'editorStyle'
    | 'editorClassName'
    | 'wrapperStyle'
    | 'wrapperClassName'
    | 'placeholder'
  > {
  // HTML String
  defaultValue?: string
  onChange?: (html: string) => void
}

const Editor = lazy(async () => {
  const { Editor } = await import('react-draft-wysiwyg')
  return { default: Editor }
})

export const RichTextEditor = forwardRef<WYSIWYGEditor, RichTextEditorProps>(
  (
    {
      defaultValue,
      onChange: customOnChange,
      editorStyle,
      toolbarStyle,
      wrapperStyle,
      ...rest
    },
    ref
  ): JSX.Element => {
    const [editorState, setEditorState] = useState<EditorState>()

    useEffect(() => {
      const contentBlock = convertFromHTML(
        typeof defaultValue === 'string' ? defaultValue : ''
      )
      let editorState: EditorState
      if (contentBlock) {
        const contentState = ContentState.createFromBlockArray(
          contentBlock.contentBlocks,
          contentBlock.entityMap
        )
        editorState = EditorState.createWithContent(contentState)
      } else {
        editorState = EditorState.createEmpty()
      }

      setEditorState(editorState)
    }, [defaultValue, setEditorState])

    const onChange = (contentState: RawDraftContentState): void => {
      if (typeof customOnChange === 'function') {
        const html = draftToHtml(contentState)
        customOnChange(html)
      }
    }

    const onEditorStateChange = (editorState: EditorState): void => {
      setEditorState(editorState)
    }

    return (
      <>
        <Suspense fallback={null}>
          <Editor
            ref={ref}
            locale={'ja'}
            editorStyle={{
              backgroundColor: 'rgba(255, 255, 255, var(--tw-bg-opacity))',
              border: '1px solid #d9d9d9',
              borderTop: 0,
              borderRadius: '2px',
              cursor: 'text',
              padding: '8px 16px',
              minHeight: '14rem',
              ...editorStyle,
            }}
            toolbarStyle={{
              backgroundColor: 'rgba(249, 250, 251, var(--tw-bg-opacity))',
              border: '1px solid #d9d9d9',
              marginBottom: '0',
              ...toolbarStyle,
            }}
            wrapperStyle={{
              backgroundColor: 'rgba(255, 255, 255, var(--tw-bg-opacity))',
              ...wrapperStyle,
            }}
            editorState={editorState}
            onChange={onChange}
            onEditorStateChange={onEditorStateChange}
            {...rest}
          />
        </Suspense>
      </>
    )
  }
)
